import type { ApproachPerspectiveAssignment, DeploymentInputType } from 'types/approach';
import type { ChannelItemType } from 'types/channel';
import type { MarketplaceItemType } from '../../components/atoms/marketplace/ItemTypeGlyphAtom';

/**
 * The discriminator value of `ApproachPerspectiveAssignment.assigneeSource.type`.
 * Hoisted into a flat string-literal union so static maps can be exhaustive.
 */
export type AssigneeSourceType = ApproachPerspectiveAssignment['assigneeSource']['type'];

export interface LookupMeta {
  /** PrimeIcon class (without the leading `pi pi-`). */
  icon: string;
  /** i18n key — caller resolves with `t()`. */
  labelKey: string;
}

function assertNever(value: never): never {
  throw new Error(`Unhandled lookup variant: ${String(value)}`);
}

const ASSIGNEE_SOURCE_META: Record<AssigneeSourceType, LookupMeta> = {
  inherit_from_parent: {
    icon: 'pi-arrow-circle-up',
    labelKey: 'marketplace.approachDetail.assigneeSource.inheritFromParent',
  },
  specific_sphere: {
    icon: 'pi-bullseye',
    labelKey: 'marketplace.approachDetail.assigneeSource.specificSphere',
  },
  user_selects: {
    icon: 'pi-user-edit',
    labelKey: 'marketplace.approachDetail.assigneeSource.userSelects',
  },
  role: {
    icon: 'pi-key',
    labelKey: 'marketplace.approachDetail.assigneeSource.role',
  },
  created_child: {
    icon: 'pi-plus-circle',
    labelKey: 'marketplace.approachDetail.assigneeSource.createdChild',
  },
  deploying_user: {
    icon: 'pi-user',
    labelKey: 'marketplace.approachDetail.assigneeSource.deployingUser',
  },
  parent_responsible: {
    icon: 'pi-sitemap',
    labelKey: 'marketplace.approachDetail.assigneeSource.parentResponsible',
  },
  provided_resource: {
    icon: 'pi-box',
    labelKey: 'marketplace.approachDetail.assigneeSource.providedResource',
  },
};

const INPUT_TYPE_META: Record<DeploymentInputType, LookupMeta> = {
  text: {
    icon: 'pi-pencil',
    labelKey: 'marketplace.approachDetail.deploymentInputs.inputTypes.text',
  },
  number: {
    icon: 'pi-hashtag',
    labelKey: 'marketplace.approachDetail.deploymentInputs.inputTypes.number',
  },
  date: {
    icon: 'pi-calendar',
    labelKey: 'marketplace.approachDetail.deploymentInputs.inputTypes.date',
  },
  date_range: {
    icon: 'pi-calendar-clock',
    labelKey: 'marketplace.approachDetail.deploymentInputs.inputTypes.dateRange',
  },
  select: {
    icon: 'pi-list',
    labelKey: 'marketplace.approachDetail.deploymentInputs.inputTypes.select',
  },
  person_select: {
    icon: 'pi-user',
    labelKey: 'marketplace.approachDetail.deploymentInputs.inputTypes.personSelect',
  },
  sphere_select: {
    icon: 'pi-globe',
    labelKey: 'marketplace.approachDetail.deploymentInputs.inputTypes.sphereSelect',
  },
  boolean: {
    icon: 'pi-check-square',
    labelKey: 'marketplace.approachDetail.deploymentInputs.inputTypes.boolean',
  },
};

/**
 * Static lookup for an `assigneeSource.type` discriminator.
 * Returns the icon + i18n key the caller should pass to `<SchemaChipAtom>`-style consumers.
 *
 * Exhaustive over every variant of `AssigneeSourceType` — adding a new variant
 * to the discriminated union forces a compile error here.
 */
export function getAssigneeSourceMeta(type: AssigneeSourceType): LookupMeta {
  const entry = ASSIGNEE_SOURCE_META[type];
  if (entry) return entry;
  return assertNever(type as never);
}

/**
 * Static lookup for a deployment-input `inputType`. Returns the icon + i18n key
 * the consumer renders next to the input label.
 *
 * Exhaustive over every variant of `DeploymentInputType` (aliased as `ApproachInputType`
 * in the spec); adding a new variant forces a compile error here.
 */
export function getInputTypeMeta(inputType: DeploymentInputType): LookupMeta {
  const entry = INPUT_TYPE_META[inputType];
  if (entry) return entry;
  return assertNever(inputType as never);
}

// Every template-supertype channel item type (schema/context/arc_template/
// orbit_template/metaspace) now has a first-class MarketplaceItemType glyph,
// so it maps to itself. surface_template and archetype have no dedicated
// glyph and keep a "closest visual analog" alias — archetype is a
// classification vocabulary on a sphere, the same role `aspect` plays here.
const CHANNEL_ITEM_TYPE_TO_MARKETPLACE_TYPE: Record<ChannelItemType, MarketplaceItemType> = {
  approach: 'approach',
  surface_template: 'evaluation',
  assistant: 'assistant',
  video: 'video',
  form: 'form',
  metaspace: 'metaspace',
  schema: 'schema',
  context: 'context',
  arc_template: 'arc_template',
  orbit_template: 'orbit_template',
  aspect: 'aspect',
  aspect_group: 'aspect_group',
  composition: 'composition',
  archetype: 'aspect',
};

/**
 * Resolve the `ItemTypeGlyphAtom` type for a channel item's `item_type`.
 *
 * Exhaustive over every variant of `ChannelItemType`; adding a new channel
 * item type forces a compile error here until it's given a mapping.
 */
export function getMarketplaceItemType(itemType: ChannelItemType): MarketplaceItemType {
  const entry = CHANNEL_ITEM_TYPE_TO_MARKETPLACE_TYPE[itemType];
  if (entry) return entry;
  return assertNever(itemType as never);
}
